// Dev store — client side of local dev mode. Asks GET /api/dev/status once; in production that route
// does not exist (404), so the store stays "off" and every dev seam is a pass-through. The active dev
// identity is per tab (sessionStorage), so isolated tabs/windows can be different people.

import { useSyncExternalStore } from 'react';

export type DevIdentity = Readonly<{
  readonly key: string;
  readonly label: string;
  readonly role: 'merchant' | 'employee' | 'customer';
  readonly handle: string | null;
  readonly address: string;
}>;

export type DevStatus = Readonly<{
  readonly txPrefix: string;
  readonly identities: ReadonlyArray<DevIdentity>;
}>;

export type DevState = Readonly<{
  readonly status: DevStatus | null;   // null = dev mode off (or not known yet)
  readonly checked: boolean;           // /api/dev/status answered (or failed)
  readonly activeKey: string | null;
}>;

const STORAGE_KEY = 'chainsale.dev.identity';

const _readKey = (): string | null => {
  try { return sessionStorage.getItem(STORAGE_KEY); } catch { return null; }
};
const _writeKey = (key: string | null): void => {
  try {
    if (key === null) sessionStorage.removeItem(STORAGE_KEY);
    else sessionStorage.setItem(STORAGE_KEY, key);
  } catch { /* storage blocked — dev identity lasts until reload */ }
};

let _state: DevState = Object.freeze({ status: null, checked: false, activeKey: _readKey() });
const _listeners = new Set<() => void>();
let _requested = false;

const _set = (next: DevState): void => {
  _state = Object.freeze(next);
  _listeners.forEach((l) => l());
};

// Parse the /api/dev/status payload; anything unexpected = dev mode off
export const parseDevStatus = (json: unknown): DevStatus | null => {
  const data = (json as { data?: { devMode?: unknown; txPrefix?: unknown; identities?: unknown } } | null)?.data;
  if (data?.devMode !== true || typeof data.txPrefix !== 'string' || !Array.isArray(data.identities)) return null;
  return Object.freeze({ txPrefix: data.txPrefix, identities: Object.freeze(data.identities as DevIdentity[]) });
};

const _requestStatus = (): void => {
  if (_requested) return;
  _requested = true;
  fetch('/api/dev/status')
    .then((r) => (r.ok ? r.json() : null))
    .then((json) => {
      const status = parseDevStatus(json);
      // A stored identity from an old dev session is ignored when dev mode is off
      _set({ status, checked: true, activeKey: status === null ? null : _state.activeKey });
    })
    .catch(() => { _set({ ..._state, checked: true }); /* production / offline: dev mode stays off */ });
};

const _subscribe = (listener: () => void): (() => void) => {
  _requestStatus();
  _listeners.add(listener);
  return () => { _listeners.delete(listener); };
};

export const useDevState = (): DevState => useSyncExternalStore(_subscribe, () => _state, () => _state);

export const setActiveDevIdentity = (key: string | null): void => {
  _writeKey(key);
  _set({ ..._state, activeKey: key });
};

// The identity this tab is acting as, only while the server reports dev mode
export const activeDevIdentity = (state: DevState): DevIdentity | null =>
  state.status === null || state.activeKey === null
    ? null
    : state.status.identities.find((d) => d.key === state.activeKey) ?? null;

// A saved dev identity whose dev-mode check is still in flight — the connection is being restored
export const isDevIdentityRestoring = (state: DevState): boolean =>
  !state.checked && state.activeKey !== null;
